import { Box, TouchableBox } from "arkh";
import React, { Suspense, useCallback } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { TodoEditControllProps } from "./TodoEditNewMain";

import { TodoEditNewMainMedia } from "./TodoEditNewMainMedia";
import { TodoEditNewMainToolbar } from "./TodoEditNewMainToolbar";

import { TodoEditNewMainStoreTitleAndDescription } from "./TodoEditNewMainStoreTitleAndDescription";
import { TodoEditNewMainUniversal_todo$key } from "./__generated__/TodoEditNewMainUniversal_todo.graphql";
import { TodoEditNewMainSubTodos } from "./TodoEditNewMainSubTodos";
import { TodoEditNewMainParentIndicator } from "./TodoEditNewMainParentIndicator";
import { TodoEditNewMainOrgTitleAndDescription } from "./TodoEditNewMainOrgTitleAndDescription";
import { TodoEditNewMainCreationInfo } from "arkh/src/cross-platform/Todo/TodoEditNewMainCreationInfo";
import { AttachedContentTodo } from "@/src/components/LinkedContent/AttachedContent";
import { useTodoUploadImageLink } from "./todoMutationHooks/useTodoUploadImageLink";
import { TodoAttachmentControllsWithPrepare } from "./TodoAttachmentControlls";
import { TodoContact } from "widgets";

export function TodoEditNewMainUniversal(
  props: {
    todo: TodoEditNewMainUniversal_todo$key;
    setStatus: (key: string, status: "saving" | "saved" | "error") => void;
    inBottomSheet: boolean;
    TitleComponent?: React.ReactNode;
    highlightCamera?: boolean;
    openTodo: (
      mode: "sub" | "parent",
      fromResponse: boolean,
      todoId: string
    ) => void;
    onRequestLinkProduct: () => void;
  } & TodoEditControllProps
) {
  const todo = useFragment(
    graphql`
      fragment TodoEditNewMainUniversal_todo on Todo {
        id

        ...TodoEditNewMainCreationInfo
        ...TodoEditNewMainStoreTitleAndDescription_todo
        ...TodoEditNewMainMediaComponentMedia_todo
        ...TodoEditNewMainToolbar_todo
        ...TodoEditNewMainParentIndicator_todo
        ...TodoEditNewMainOrgTitleAndDescription_todo
        ...AttachedContentTodo_todo
        ...TodoContact_todo

        canWrite
        isOrg
        ...TodoEditNewMainSubTodos_todo
      }
    `,
    props.todo
  );
  const [prepareUploadLink] = useTodoUploadImageLink(todo.id);

  const { openTodo } = props;
  const openSubTodos = useCallback(
    (fromResponse: boolean, todoId: string) => {
      openTodo("sub", fromResponse, todoId);
    },
    [openTodo]
  );
  const readOnly = !todo.canWrite || todo.isOrg;
  return (
    <>
      <Box
        color="surface-primary"
        borderColor="border-primary"
        style={{ borderBottomWidth: 1 }}
      >
        <Suspense fallback={null}>
          <TodoEditNewMainParentIndicator
            onPress={(parentId) => props.openTodo("parent", false, parentId)}
            todo={todo}
          />
        </Suspense>

        {todo.isOrg ? (
          <>
            <TodoEditNewMainCreationInfo todo={todo} showLockIcon />
            <TodoEditNewMainOrgTitleAndDescription todo={todo} />
          </>
        ) : (
          <>
            <TodoEditNewMainStoreTitleAndDescription
              TitleComponent={props.TitleComponent}
              todo={todo}
              setStatus={props.setStatus}
            />
          </>
        )}
        <Suspense fallback={null}>
          <TodoEditNewMainMedia
            todo={todo}
            highlightCamera={props.highlightCamera ?? false}
          />
          <AttachedContentTodo
            todo={todo}
            openFile={props.openFile}
            openProduct={props.openProduct}
            canEditAttachedContent={!readOnly}
          />
        </Suspense>
        {!readOnly ? (
          <TodoAttachmentControllsWithPrepare
            highlightCamera={props.highlightCamera}
            onRequestLinkProduct={props.onRequestLinkProduct}
            onPrepareUploadLink={prepareUploadLink}
          />
        ) : null}

        <Suspense fallback={null}>
          <TouchableBox
            onPress={() => {
              props.openFeature("SEND_FEEDBACK");
            }}
          >
            <TodoContact
              todo={todo}
              boxProps={{
                spaceHorizontal: "medium",
                spaceBottom: "medium",
              }}
            />
          </TouchableBox>

          <TodoEditNewMainToolbar todo={todo} openFeature={props.openFeature} />
        </Suspense>
      </Box>
      <Suspense fallback={null}>
        <TodoEditNewMainSubTodos todo={todo} onOpenTodo={openSubTodos} />
      </Suspense>
    </>
  );
}
